import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { BadgeCheck, FileCheck2, MapPinned } from 'lucide-react';
import LeadForm from './LeadForm';
import { MaskedLine } from './Reveal';

const HERO_IMG =
  'https://images.pexels.com/photos/13795730/pexels-photo-13795730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const chipY = useTransform(scrollYProgress, [0, 1], ['0%', '-38%']);

  return (
    <section ref={ref} className="relative bg-brand-bluedark overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          fetchPriority="high"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-bluedark via-brand-bluedark/85 to-brand-blue/60" />
        <div className="absolute inset-0 hero-grid" />
        <div className="absolute -top-32 -right-24 w-[560px] h-[560px] rounded-full bg-brand-orange/20 blur-[140px]" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-brand-bluelight/25 blur-[120px]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 backdrop-blur px-4 py-1.5"
            data-testid="hero-eyebrow"
          >
            <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
            <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em] text-white/90">
              India's Traffic Challan Legal Settlement Platform
            </span>
          </motion.div>

          <h1
            className="font-display font-bold tracking-tight text-white leading-[1.04] mt-6 text-[2.6rem] sm:text-6xl lg:text-[4.4rem]"
            data-testid="hero-headline"
          >
            <MaskedLine delay={0.15}>Got Traffic Challans?</MaskedLine>
            <MaskedLine delay={0.28}>
              Settle Them <span className="text-brand-orange italic">Legally</span>
            </MaskedLine>
            <MaskedLine delay={0.41}>&amp; Hassle-Free</MaskedLine>
            <MaskedLine delay={0.54}>
              Across India.
              <svg viewBox="0 0 220 12" className="inline-block w-[0.9em] h-[0.22em] ml-2 -mb-1" aria-hidden="true">
                <motion.path
                  d="M4 8 C 60 2, 160 2, 216 7"
                  fill="none"
                  stroke="#FF7700"
                  strokeWidth="5"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.7, delay: 1.1, ease: 'easeOut' }}
                />
              </svg>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: 'easeOut' }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-white/75"
            data-testid="hero-subheadline"
          >
            Avoid court visits, license suspension, or heavy penalties. Authorized legal assistance to settle, contest,
            and dispose of pending traffic challans at virtual courts and Lok Adalats.
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9, ease: 'easeOut' }}
            className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-6"
            data-testid="hero-bullets"
          >
            {[
              { icon: BadgeCheck, text: '0 Court Visits Required' },
              { icon: FileCheck2, text: 'Official Court Receipt' },
              { icon: MapPinned, text: 'Pan-India Coverage' },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2 text-sm font-semibold text-white/90">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-brand-orange/20 text-brand-orange">
                  <Icon size={14} strokeWidth={2.5} />
                </span>
                {text}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="lg:col-span-5 relative" data-testid="hero-form-card">
          <motion.div
            initial={{ opacity: 0, y: 40, rotate: 1.5 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 0.85, delay: 0.5, ease: [0.215, 0.61, 0.355, 1] }}
            className="relative"
          >
            <motion.div
              style={{ y: chipY }}
              className="absolute -top-5 -left-3 sm:-left-8 z-20 hidden sm:flex items-center gap-2.5 rounded-xl bg-white shadow-2xl shadow-black/20 px-4 py-3 animate-floaty"
              data-testid="hero-floating-chip"
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-green-100 text-green-600">
                <FileCheck2 size={18} strokeWidth={2.5} />
              </span>
              <span>
                <span className="block text-xs font-bold text-brand-ink">Challan Disposed</span>
                <span className="block text-[10px] font-semibold text-slate-500">Status updated on Parivahan</span>
              </span>
            </motion.div>

            <div className="rounded-2xl bg-white/95 backdrop-blur-xl border border-white/60 p-6 sm:p-8 lead-card">
              <h2 className="font-display text-2xl sm:text-[1.7rem] font-bold text-brand-ink leading-tight" data-testid="hero-form-title">
                Check &amp; Settle Your <span className="text-brand-orange">Challan Now</span>
              </h2>
              <p className="mt-1.5 mb-6 text-xs font-medium text-slate-500">
                Free assessment. Quote on WhatsApp within minutes.
              </p>
              <LeadForm variant="hero" source="hero" />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="relative z-10 h-6 bg-gradient-to-b from-transparent to-[#F8FAFC]/0" />
    </section>
  );
}
